import React, { useState } from 'react';

const ProductFeedback = ({ messageIndex, onRefineResults }) => {
  const [feedbackState, setFeedbackState] = useState('initial'); // 'initial', 'thumbs', 'submitted'
  const [selectedVote, setSelectedVote] = useState(null); // 'up' or 'down'
  const [selectedReasons, setSelectedReasons] = useState([]);
  const [otherText, setOtherText] = useState('');
  const [showOther, setShowOther] = useState(false);

  const positiveReasons = [
    { id: 'matched_shade', label: 'Matched my shade' },
    { id: 'good_price', label: 'Good price' },
    { id: 'available_buy', label: 'Available to buy' },
    { id: 'like_texture', label: 'Like the texture/finish' },
    { id: 'brand_trust', label: 'Brand I trust' }
  ];

  const negativeReasons = [
    { id: 'too_expensive', label: 'Too expensive' },
    { id: 'out_of_stock', label: 'Out of stock' },
    { id: 'wrong_shade', label: 'Wrong undertone/shade' },
    { id: 'not_available_country', label: 'Not available in my country' },
    { id: 'allergic_sensitive', label: 'Allergic/sensitive' },
    { id: 'dont_like_texture', label: "Don't like finish/texture" }
  ];

  const handleVote = (vote) => {
    setSelectedVote(vote);
    setFeedbackState('thumbs');
    setSelectedReasons([]);
    setShowOther(false);
    setOtherText('');
  };

  const handleReasonToggle = (reasonId) => {
    setSelectedReasons(prev => 
      prev.includes(reasonId) 
        ? prev.filter(id => id !== reasonId)
        : [...prev, reasonId]
    );
  };

  const handleSubmitFeedback = () => {
    // TODO: Send feedback to backend when implemented
    const feedbackData = {
      messageIndex,
      vote: selectedVote,
      reasons: selectedReasons,
      otherText: otherText.trim() || null,
      timestamp: new Date().toISOString()
    };
    
    console.log('Feedback submitted:', feedbackData);
    setFeedbackState('submitted');
    
    // Auto-trigger refine if negative feedback
    if (selectedVote === 'down' && selectedReasons.length > 0) {
      setTimeout(() => {
        handleRefineResults();
      }, 1000);
    }
  };

  const handleRefineResults = () => {
    const refineFilters = {};
    
    // Map reasons to refinement filters
    if (selectedReasons.includes('too_expensive')) {
      refineFilters.budget_concern = true;
    }
    if (selectedReasons.includes('wrong_shade')) {
      refineFilters.shade_issue = true;
    }
    if (selectedReasons.includes('not_available_country')) {
      refineFilters.location_issue = true;
    }
    if (selectedReasons.includes('dont_like_texture') || selectedReasons.includes('allergic_sensitive')) {
      refineFilters.texture_sensitivity = true;
    }
    
    onRefineResults(refineFilters);
  };

  if (feedbackState === 'submitted') {
    return (
      <div className="mt-3 p-3 bg-green-50 rounded-lg border border-green-200">
        <div className="flex items-center gap-2 text-green-700 text-sm">
          <span>✓</span>
          <span>Thanks for your feedback! We'll improve your recommendations.</span>
        </div>
      </div>
    );
  }

  return (
    <div className="mt-3 p-3 bg-gray-50 rounded-lg border border-gray-200">
      {feedbackState === 'initial' && (
        <div className="flex items-center justify-between">
          <span className="text-sm text-gray-600">Was this helpful?</span>
          <div className="flex gap-2">
            <button
              onClick={() => handleVote('up')}
              className="flex items-center gap-1 px-3 py-1.5 text-sm bg-white border border-gray-300 rounded-lg hover:bg-green-50 hover:border-green-300 transition-colors"
            >
              👍 Helpful
            </button>
            <button
              onClick={() => handleVote('down')}
              className="flex items-center gap-1 px-3 py-1.5 text-sm bg-white border border-gray-300 rounded-lg hover:bg-red-50 hover:border-red-300 transition-colors"
            >
              👎 Not helpful
            </button>
          </div>
        </div>
      )}

      {feedbackState === 'thumbs' && (
        <div className="space-y-3">
          <div className="text-sm text-gray-600">
            Why was this {selectedVote === 'up' ? 'helpful' : 'not helpful'}?
          </div>
          
          <div className="flex flex-wrap gap-2">
            {(selectedVote === 'up' ? positiveReasons : negativeReasons).map(reason => (
              <button
                key={reason.id}
                onClick={() => handleReasonToggle(reason.id)}
                className={`px-3 py-1.5 text-sm rounded-full border transition-colors ${
                  selectedReasons.includes(reason.id)
                    ? 'bg-blue-100 border-blue-300 text-blue-700'
                    : 'bg-white border-gray-300 text-gray-700 hover:bg-gray-50'
                }`}
              >
                {reason.label}
              </button>
            ))}
            <button
              onClick={() => setShowOther(!showOther)}
              className={`px-3 py-1.5 text-sm rounded-full border transition-colors ${
                showOther
                  ? 'bg-blue-100 border-blue-300 text-blue-700'
                  : 'bg-white border-gray-300 text-gray-700 hover:bg-gray-50'
              }`}
            >
              Other
            </button>
          </div>

          {showOther && (
            <input
              type="text"
              placeholder="Tell us more (optional)"
              value={otherText}
              onChange={(e) => setOtherText(e.target.value)}
              maxLength={120}
              className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          )}

          <div className="flex gap-2 justify-end">
            {selectedVote === 'down' && (
              <button
                onClick={handleRefineResults}
                className="px-4 py-2 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
              >
                Refine my results
              </button>
            )}
            <button
              onClick={handleSubmitFeedback}
              className="px-4 py-2 text-sm bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors"
            >
              Submit
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProductFeedback; 